import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Spacing, TagButton, Text } from '@reservamos/elements';
import { change, Field } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { trackEvent } from 'user-analytics';
import { updateFirstPassenger } from '@/actions/siemprePlus';
import FrequentPassengerModal from 'components/FrequentPassengerModal';
import FrequentPassengerNav from 'components/FrequentPassengerNav';
import useFrequentPassengersContext from 'context/FrequentPassengers';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import { fieldRenderer } from 'utils/formRenderers';
import OverbookingBadge from 'ui/atoms/OverbookingBadge';
import CleanButton from 'ui/atoms/CleanButton';
import { getOversoldAvailability } from '../../../utils/overbooking';
import PassengerBusCategories from './PassengerBusCategories';
import PassengerInsuranceCheckbox from '../../PassengerInsuranceCheckbox';
import DotersLoyaltyPassengerView from './DotersLoyaltyPassengerView';
import DotersGuestPurchaseSwitch from '../../../ui/molecules/DotersGuestPurchaseSwitch';
import normalizeName from '../../../utils/normalizeName';
import normalizeFirstName from '../../../utils/normalizeFirstName';
import normalizeEmail from '../../../utils/normalizeEmail';
import PassengersHeading from '../../../ui/molecules/PassengersHeading';
import LabelSwitch from '../../../ui/atoms/LabelSwitch';
import LoyaltyHolder from '../../../ui/atoms/LoyaltyHolder';
import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import usePurchase from '../../../hooks/store/usePurchase';
import parsePassenger from '../../../utils/purchase/parsePassenger';
import { FLEXIBLE_EDITABLE_FIELDS } from '../../../utils/dotersFlexibleEditing';
import { trackFlexibleNameChange } from '../../../metrics/user-analytics/loyalty';
import { passengerAutoComplete } from '../../../utils/fieldAutoComplete';

const propTypes = {
  fields: PropTypes.object.isRequired,
  autofillEnabled: PropTypes.bool.isRequired,
  updatingCategories: PropTypes.bool.isRequired,
  busCategories: PropTypes.array,
  toggleAutofill: PropTypes.func.isRequired,
  deletePassenger: PropTypes.func.isRequired,
  insuranceOutgoingUnitAmount: PropTypes.number,
  insuranceIncomingUnitAmount: PropTypes.number,
  isExchange: PropTypes.bool,
  isOpenTicket: PropTypes.bool,
  loyaltyHolderName: PropTypes.string,
  user: PropTypes.object,
  isLogged: PropTypes.bool,
  isAccountThePassenger: PropTypes.bool,
  dynamicInitialValues: PropTypes.array,
  autofillFirstPassenger: PropTypes.func,
  overbooking: PropTypes.bool,
};

const defaultProps = {
  busCategories: [],
  isExchange: false,
};

const DynamicPassengersForm = (props) => {
  const [scrollBottom, setScrollBottom] = useState(false);
  const [holderEditingEnabled, setHolderEditingEnabled] = useState(false);
  const [frequentPassengersModal, setFrequentPassengersModal] = useState({
    opened: false,
    passengerIndex: -1,
    passengerCategory: '',
  });
  const initialFields = useRef(null);

  const dispatch = useDispatch();
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('passengers');
  const showFrequentPassengers = useExperimentalFeature('SHOW_FREQUENT_PASSENGERS');
  const { frequentPassengers } = useFrequentPassengersContext();

  const { availableWallets = [], walletType } = usePurchase();
  const { userIsLoggedInWithAnyLoyaltyProgram, doters } = useLoyaltyPrograms();

  const {
    fields,
    busCategories,
    autofillEnabled,
    updatingCategories,
    toggleAutofill,
    insuranceOutgoingUnitAmount,
    insuranceIncomingUnitAmount,
    isExchange,
    isOpenTicket,
    loyaltyHolderName,
    user,
    isLogged,
    isAccountThePassenger,
    overbooking,
  } = props;

  // Get oversold availability from busCategories when overbooking is active
  const oversoldAvailability = overbooking ? getOversoldAvailability(busCategories) : null;

  // Calculate the effective limit considering both system limit and oversold availability
  const systemLimit = features.SEAT_SELECTION_LIMIT;
  const effectiveLimit =
    overbooking && oversoldAvailability !== null
      ? Math.min(systemLimit, oversoldAvailability)
      : systemLimit;

  const maxPassengersReached = fields.length >= effectiveLimit;

  /**
   * DynamicPassengersForm component.
   * Renders a dynamic form for passenger information with support for overbooking scenarios.
   * When overbooking is active, the number of passengers is limited by the oversold availability
   * from busCategories, which takes precedence over the system limit.
   */

  // Define default passenger with dynamic busCategory based on overbooking
  const defaultPassenger = {
    firstName: '',
    lastName: '',
    category: 'adult',
    busCategory: overbooking ? 'oversold' : 'general',
  };

  /**
   * Sets the initial values of passengers in a static ref array
   * This field is used for avoid render without initializing and for load initial data after authenticate
   */
  const setInitialFields = () => {
    initialFields.current = [...fields.getAll()];
  };

  useEffect(() => {
    if (!initialFields.current) setInitialFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { dynamicInitialValues, autofillFirstPassenger } = props;
    if (isLogged) {
      autofillFirstPassenger(
        dynamicInitialValues[0].firstName,
        dynamicInitialValues[0].lastName,
        dynamicInitialValues[0].secondLastName,
        dynamicInitialValues[0].documentType,
        dynamicInitialValues[0].documentId,
        dynamicInitialValues[0].email,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged]);

  const onDeletePassenger = (index) => {
    const { fields, deletePassenger } = props;
    const passengerId = fields.get(index).id;

    if (passengerId) {
      deletePassenger(passengerId);
    }

    fields.remove(index);
  };

  const addPassenger = () => {
    const { fields } = props;
    if (maxPassengersReached) return;
    fields.push(defaultPassenger);
    setScrollBottom(true);
  };

  /**
   * Handles the name change switch click event.
   * When clicked, if disabled it sets default values for firstName and lastName
   * @param {SyntheticEvent} e - Click event
   */
  const handleHolderEditing = (e) => {
    if (!e.target.checked) {
      fields.get(0).firstName = initialFields.current[0]?.firstName;
      fields.get(0).lastName = initialFields.current[0]?.lastName;
    }
    setHolderEditingEnabled((prev) => !prev);
    if (holderEditingEnabled) {
      updateFirstPassenger(parsePassenger(user));
    }
  };

  /**
   * Handles guest purchase confirmation from DotersGuestPurchaseSwitch
   * @param {boolean} isEnabled - Whether guest purchase mode is enabled
   */
  const handleGuestPurchaseConfirm = (isEnabled) => {
    handleHolderEditing({ target: { checked: isEnabled } });
  };

  /**
   * Handler for name field changes in flexible mode
   * Tracks analytics when user changes name fields
   * Only tracks analytics for first passenger (index 0)
   */
  const handleNameFieldChange = ({ fieldName, value, passengerIndex }) => {
    const isDotersFlexible = features?.DOTERS_FLEXIBLE_PASSENGER_MATCHING?.includes('redeem');

    // Only track for the first passenger when flexible mode is enabled
    if (!isDotersFlexible || !holderEditingEnabled || passengerIndex !== 0) return;

    trackFlexibleNameChange({
      passengerIndex,
      fieldName,
      value,
      walletType,
      isCleared: !value || value.trim() === '',
    });
  };

  useEffect(() => {
    if (!isLogged) return;
    if (isAccountThePassenger) {
      setHolderEditingEnabled(false);
    } else {
      setHolderEditingEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAccountThePassenger]);

  useEffect(() => {
    if (scrollBottom) {
      window.scrollTo(0, document.body.scrollHeight);
      setScrollBottom(false);
    }
  }, [scrollBottom]);

  const SHOW_PURCHASER_FORM = features.PURCHASER_FORM_ON === 'PASSENGERS';

  const passengers = fields.map((passenger, index) => {
    const passengerCategory = fields.get(index).category;
    const passengerFields = fields.get(index);
    const isFirstPassenger = index === 0;
    const fadeIn = isFirstPassenger ? '' : 'animated fade-in';
    const initialValues =
      initialFields.current && initialFields.current[index]
        ? initialFields.current[index]
        : passengerFields;

    const isDisabled = (config) => {
      const { fieldValue, fieldName, isNameField = false } = config;

      // Check if this is a REAL name field that can be edited in Doters flexible mode
      // Only true name fields (firstName, lastName, etc.) should be treated as flexible editable
      // Document fields like documentId should NOT be treated as flexible editable, even if they simulate being firstName
      // IMPORTANT: This exception is ONLY for Doters wallet, not other loyalty programs
      const isFlexibleEditableField =
        loyaltyHolderName === 'doters' &&
        features.DOTERS_FLEXIBLE_PASSENGER_MATCHING?.includes('redeem') &&
        isNameField &&
        FLEXIBLE_EDITABLE_FIELDS.includes(fieldName);

      const exchangeDisablingCondition = Boolean(
        isExchange && !features.EXCHANGE_PASSENGERS_EDITABLE && fieldValue,
      );

      const disableBecauseAutoFill = fieldName !== 'phone' && autofillEnabled && isFirstPassenger;

      const enabledEmailField = fieldName === 'email' && !features.SHOW_PASSENGERS_EMAIL_FIELD;

      // For flexible editable name fields, only disable if toggle is NOT enabled
      // For other fields, use standard loyalty disabling condition
      const loyaltyDisablingCondition =
        availableWallets?.includes(walletType) &&
        userIsLoggedInWithAnyLoyaltyProgram &&
        isLogged &&
        isFirstPassenger &&
        loyaltyHolderName &&
        features.DISABLE_PASSENGER_LOGGED &&
        // Exception: don't disable flexible editable name fields when toggle is enabled
        // This exception only applies to Doters (checked in isFlexibleEditableField)
        !(isFlexibleEditableField && holderEditingEnabled);

      return (
        loyaltyDisablingCondition ||
        exchangeDisablingCondition ||
        disableBecauseAutoFill ||
        enabledEmailField
      );
    };

    // Determine if Doters flexible mode is enabled
    const isDotersFlexibleMode = features.DOTERS_FLEXIBLE_PASSENGER_MATCHING?.includes('redeem');

    const canUseEditSwitch = {
      costapass: false,
      siempreplus: true,
      // Doters doesn't use showEditNameSwitch, uses DotersGuestPurchaseSwitch instead
    };
    const showLoyaltyHolder =
      loyaltyHolderName && isFirstPassenger && userIsLoggedInWithAnyLoyaltyProgram;

    // Show edit name switch only for loyalty programs that support it
    const showEditNameSwitch =
      showLoyaltyHolder &&
      !isExchange &&
      (canUseEditSwitch[loyaltyHolderName] ?? false) &&
      isFirstPassenger;

    const shouldHideInsuranceCheckbox =
      !features.PASSENGERS_INSURANCE_ENABLED ||
      !(insuranceIncomingUnitAmount > 0 || insuranceOutgoingUnitAmount > 0);

    // Determine if we should show the LoyaltyPassengerCard view
    // For exchange purchases, only show the Doters card if the passenger matches the Doters
    // account holder. Otherwise show regular (disabled) form fields.
    const isDotersWallet = loyaltyHolderName === 'doters';
    const dotersUser = doters?.profile;
    const passengerMatchesDotersUser =
      dotersUser &&
      passengerFields?.firstName?.trim().toUpperCase() ===
        dotersUser?.firstName?.trim().toUpperCase() &&
      passengerFields?.lastName?.trim().toUpperCase() ===
        dotersUser?.lastName?.trim().toUpperCase();
    const shouldShowPassengerInfoCard =
      isFirstPassenger &&
      isDotersWallet &&
      (!isExchange || passengerMatchesDotersUser) &&
      (!isDotersFlexibleMode || !holderEditingEnabled);

    const showDeleteButton = !isFirstPassenger && !isExchange;
    const showFrequentPassengerPicker =
      showFrequentPassengers &&
      Boolean(frequentPassengers.length) &&
      !(doters?.userIsLoggedIn && isFirstPassenger) &&
      !isExchange;

    const cleanPassenger = () => {
      dispatch(
        change('passengers', `passengers[${index}]`, {
          ...passengerFields,
          name: '',
          nationalityId: 1,
          nationality: null,
          firstName: '',
          lastName: '',
          email: '',
          secondFirstName: '',
          secondLastName: '',
          documentId: '',
          documentType: '',
          phone: '',
          gender: null,
          dateOfBirth: null,
          isFrequentPassenger: false,
          preferredPassengerOrder: false,
          selectedForPassenger: null,
        }),
      );
      trackEvent('Clean Passenger', {
        'Passenger Number': index + 1,
        'Frequent Passengers Count': frequentPassengers.length,
      });
    };

    // Reusable DotersGuestPurchaseSwitch component for first Doters passenger
    // Only show if user is logged in with Doters
    // Not shown in exchange flow since passenger names must remain locked
    const dotersGuestSwitch = !isExchange &&
      isDotersFlexibleMode &&
      isFirstPassenger &&
      isDotersWallet &&
      isLogged && (
        <DotersGuestPurchaseSwitch
          onConfirm={handleGuestPurchaseConfirm}
          initialChecked={holderEditingEnabled}
        />
      );

    return (
      <div key={passenger} className={`passenger-info ${fadeIn}`}>
        <Spacing vertical>
          <PassengersHeading
            category="adult"
            title={t('label.passenger', { index: index + 1 })}
            rightContent={
              <>
                {showDeleteButton && (
                  <button type="button" onClick={() => onDeletePassenger(index)}>
                    <Text elementType="span" weight="semibold" size="S" color="error">
                      {t('common:buttons.delete')}
                    </Text>
                    <Icon type="Cross" color="error" size="XS" hasMargin marginSide="left" />
                  </button>
                )}
                {showLoyaltyHolder && (
                  <LoyaltyHolder
                    loyaltyHolderName={loyaltyHolderName}
                    hideLabel={holderEditingEnabled}
                  />
                )}
                {overbooking && <OverbookingBadge hasIcon />}
              </>
            }
          />

          {showFrequentPassengerPicker && (
            <Spacing
              responsiveSize="XS"
              size="S"
              alignItems="flex-end"
              justifyContent="space-between"
              fullWidth
            >
              <Spacing size="XS" vertical fullWidth>
                <div className="flex -mb-1 gap-1">
                  <Icon type="emojiSparkles" size="M" />
                  <Text size="S" color="grayMedium">
                    {t('frequent_passengers.pick_a_frequent_passenger')}
                  </Text>
                </div>
                <div className="grid grid-cols-content-actions gap-4 items-center">
                  <FrequentPassengerNav passengerIndex={index + 1} />
                  <Spacing size="S" justifyContent="flex-end">
                    <button
                      onClick={() => {
                        setFrequentPassengersModal({
                          opened: true,
                          passengerIndex: index + 1,
                          passengerCategory:
                            passengerFields.busCategory === 'oversold'
                              ? 'general'
                              : passengerFields.busCategory,
                        });
                        trackEvent('Frequent Passengers Modal Opened', {
                          'Passenger Number': index + 1,
                        });
                      }}
                      type="button"
                      className="bg-white shadow-md px-control md:px-control-md py-control-y rounded-full flex items-center gap-1 md:gap-2"
                    >
                      <Text>{t('frequent_passengers.other_passenger')}</Text>
                      <Icon
                        size="S"
                        type="ChevronDown"
                        color="grayMedium"
                        hasMargin
                        marginSide="left"
                      />
                    </button>
                    <CleanButton onClick={cleanPassenger} />
                  </Spacing>
                </div>
              </Spacing>
            </Spacing>
          )}

          {shouldShowPassengerInfoCard ? (
            <>
              <DotersLoyaltyPassengerView
                passenger={passenger}
                passengerFields={passengerFields}
                isFirstPassenger={isFirstPassenger}
                busDisabled={isDisabled({
                  fieldValue: initialValues.busCategory,
                  fieldName: 'busCategory',
                  isNameField: false,
                })}
                initialValues={initialValues}
              />
              {dotersGuestSwitch}
            </>
          ) : (
            <>
              <Spacing size="S" flexGrow>
                <Field
                  autoComplete={passengerAutoComplete(index, 'given-name')}
                  name={`${passenger}.firstName`}
                  id={`${passenger}.firstName`}
                  type="text"
                  placeholder={t('fore_name')}
                  component={fieldRenderer}
                  normalize={normalizeFirstName}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.firstName,
                    fieldName: 'firstName',
                    isNameField: true,
                  })}
                  onChange={(e) => {
                    handleNameFieldChange({
                      fieldName: 'firstName',
                      value: e.target?.value || '',
                      passengerIndex: index,
                    });
                  }}
                />

                <Field
                  autoComplete={passengerAutoComplete(index, 'family-name')}
                  name={`${passenger}.lastName`}
                  id={`${passenger}.lastName`}
                  type="text"
                  placeholder={t('family_name_one')}
                  component={fieldRenderer}
                  normalize={normalizeName}
                  isDisabled={isDisabled({
                    fieldValue: initialValues.lastName,
                    fieldName: 'lastName',
                    isNameField: true,
                  })}
                  onChange={(e) => {
                    handleNameFieldChange({
                      fieldName: 'lastName',
                      value: e.target?.value || '',
                      passengerIndex: index,
                    });
                  }}
                />
              </Spacing>

              {(features.SHOW_PASSENGERS_SECOND_LAST_NAME_FIELD ||
                features.SHOW_PASSENGERS_EMAIL_FIELD) && (
                <Spacing size="S" flexGrow>
                  {features.SHOW_PASSENGERS_SECOND_LAST_NAME_FIELD && (
                    <Field
                      // No standard autocomplete token exists for a second surname, so this
                      // field stays opted out rather than borrowing `family-name`.
                      autoComplete="off"
                      name={`${passenger}.secondLastName`}
                      id={`${passenger}.secondLastName`}
                      type="text"
                      placeholder={`${t('family_name_two')} ${t('optional')}`}
                      component={fieldRenderer}
                      normalize={normalizeName}
                      isDisabled={isDisabled({
                        fieldValue: initialValues.secondLastName,
                        fieldName: 'secondLastName',
                        isNameField: true,
                      })}
                      onChange={(e) => {
                        handleNameFieldChange({
                          fieldName: 'secondLastName',
                          value: e.target?.value || '',
                          passengerIndex: index,
                        });
                      }}
                    />
                  )}
                  {features.SHOW_PASSENGERS_EMAIL_FIELD && (
                    <Field
                      autoComplete={passengerAutoComplete(index, 'email')}
                      name={`${passenger}.email`}
                      id={`${passenger}.email`}
                      type="email"
                      placeholder={`${t('email')} ${index !== 0 ? t('optional') : ''}`}
                      normalize={normalizeEmail}
                      component={fieldRenderer}
                      isDisabled={isDisabled({
                        fieldValue: initialValues.email,
                        fieldName: 'email',
                        isNameField: false,
                      })}
                    />
                  )}
                </Spacing>
              )}

              {showEditNameSwitch && (
                <LabelSwitch
                  label={t('edit_name')}
                  justifyContent="flex-end"
                  checked={holderEditingEnabled}
                  onChange={handleHolderEditing}
                />
              )}

              <PassengerBusCategories
                name={`${passenger}.busCategory`}
                passengerCategory={passengerCategory}
                isFirstPassenger={isFirstPassenger}
                busCategories={busCategories}
                isUpdating={updatingCategories}
                isDisabled={isDisabled({
                  fieldValue: initialValues.busCategory,
                  fieldName: 'busCategory',
                  isNameField: false,
                })}
                passengerBusCategory={fields.get(index).busCategory}
                isOpenTicket={isOpenTicket}
                isExchange={isExchange}
                initialBusCategory={initialValues.busCategory}
              />

              {!shouldHideInsuranceCheckbox && (
                <PassengerInsuranceCheckbox
                  passenger={passenger}
                  insuranceOutgoingUnitAmount={insuranceOutgoingUnitAmount}
                  insuranceIncomingUnitAmount={insuranceIncomingUnitAmount}
                />
              )}

              {dotersGuestSwitch}
            </>
          )}
        </Spacing>
      </div>
    );
  });

  const showPurchaserPassengerSwitch = !isExchange && SHOW_PURCHASER_FORM;

  return (
    <Spacing vertical>
      <Spacing vertical size="XS">
        <div className="passenger-detail-title">
          <Text weight="bold" size="L">
            {t('label.passengers')}
          </Text>

          {!isExchange && (
            <TagButton onClick={addPassenger} isDisabled={maxPassengersReached}>
              {t('add_passenger')}
            </TagButton>
          )}
        </div>

        {showPurchaserPassengerSwitch && (
          <LabelSwitch
            checked={autofillEnabled}
            onChange={toggleAutofill}
            id="check-passengers"
            label={t('is_the_buyer_a_passenger')}
          />
        )}
      </Spacing>
      <Spacing vertical size="L">
        {passengers}
      </Spacing>
      {!isExchange && (
        <div className="add-passenger">
          <TagButton onClick={addPassenger} isDisabled={maxPassengersReached}>
            {t('add_passenger')}
          </TagButton>
        </div>
      )}
      {frequentPassengersModal.opened && (
        <FrequentPassengerModal
          passengerIndex={frequentPassengersModal.passengerIndex}
          typePassenger={frequentPassengersModal.passengerCategory}
          onClose={() => {
            trackEvent('Frequent Passengers Modal Closed', {
              'Passenger Number': frequentPassengersModal.passengerIndex,
            });
            setFrequentPassengersModal({
              opened: false,
              passengerIndex: -1,
              passengerCategory: '',
            });
          }}
        />
      )}
    </Spacing>
  );
};

DynamicPassengersForm.propTypes = propTypes;
DynamicPassengersForm.defaultProps = defaultProps;

export default DynamicPassengersForm;
